<template>
    <section
        :id="slice.primary.anchorName || undefined"
        :data-slice-type="slice.slice_type"
        :data-slice-variation="slice.variation"
        :data-mui-theme="theme"
        class="slice slice-embeded slice-embeded--embeded-demo"
        aria-label="demo"
        data-testid="slice-embeded-demo"
    >
        <div class="slice-content">
            <div class="slice-embeded--embeded-demo__stage">
                <div class="slice-embeded--embeded-demo__backdrop mui-background--blur" aria-hidden="true">
                    <div class="slice-embeded--embeded-demo__background mui-background" />
                    <div class="mui-shape" />
                    <NuxtImg
                        v-if="slice.primary.coverImage.url"
                        :src="slice.primary.coverImage.url"
                        :alt="slice.primary.coverImage.alt ?? undefined"
                        class="slice-embeded--embeded-demo__cover"
                        data-testid="slice-embeded-demo-cover-image"
                    />
                </div>
                <div class="slice-embeded--embeded-demo__content">
                    <PrismicRichText
                        v-if="isFilled.richText(slice.primary.title)"
                        class="slice-title mui-title"
                        :field="slice.primary.title"
                        data-testid="slice-embeded-demo-title"
                    />
                    <PrismicRichText
                        v-if="isFilled.richText(slice.primary.description)"
                        class="slice-description"
                        :field="slice.primary.description"
                        data-testid="slice-embeded-demo-description"
                    />
                    <VJoyDialogTrigger v-if="isFilled.keyText(slice.primary.ctaStartLabel)" :dialog="demoDialogForTrigger">
                        <VJoyButton variant="main" data-testid="slice-embeded-demo-cta" class="mui-cta-neutral">
                            {{ slice.primary.ctaStartLabel }}
                        </VJoyButton>
                    </VJoyDialogTrigger>
                </div>
            </div>
        </div>
        <MuiDialogFullscreen ref="demoDialog" :theme="theme" data-testid="slice-embeded-demo-dialog" @dialog:after-hide="onDemoUnloaded">
            <VJoySpinner v-if="!isDemoLoaded" color="teal" />
            <iframe
                v-if="isFilled.keyText(slice.primary.demoUrl)"
                :src="slice.primary.demoUrl"
                frameborder="0"
                class="slice-embeded--embeded-demo__iframe"
                data-testid="slice-embeded-demo-iframe"
                @load="onDemoLoaded"
            />
        </MuiDialogFullscreen>
    </section>
</template>

<script setup lang="ts">
    import {computed, ref, useTemplateRef} from 'vue';
    import {isFilled} from '@prismicio/helpers';
    import {getSliceComponentProps, PrismicRichText} from '@prismicio/vue';
    import type {EmbededSlice} from '@prismicio-types';
    import {MuiDialogFullscreen, NuxtImg} from '#components';
    import {VJoyButton, VJoyDialogTrigger, VJoySpinner} from '@maltjoy/core-vue';

    type EmbededDemoInZone = Extract<EmbededSlice, {variation: 'embededDemo'}>;

    const props = defineProps(getSliceComponentProps<EmbededDemoInZone>());
    const {slice} = props;

    const theme = computed(() => slice.primary.theme);

    const demoDialog = useTemplateRef<InstanceType<typeof MuiDialogFullscreen>>('demoDialog');
    // MuiDialogFullscreen exposes show/hide; VJoyDialogTrigger is typed for VJoyDialog only.
    const demoDialogForTrigger = computed(() => demoDialog.value as any);
    const {isDemoLoaded, onDemoLoaded, onDemoUnloaded} = getDemo();

    function getDemo() {
        const isDemoLoaded = ref(false);

        function onDemoLoaded() {
            setTimeout(() => (isDemoLoaded.value = true), 500);
        }

        function onDemoUnloaded() {
            isDemoLoaded.value = false;
        }

        return {
            isDemoLoaded,
            onDemoLoaded,
            onDemoUnloaded,
        };
    }
</script>

<style lang="scss">
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/media-queries/media-queries' as mq;
    @use '@malt/joy-entrypoint/src/sass/base/2_mixins/text' as text;

    .slice-embeded {
        &--embeded-demo {
            &__stage {
                position: relative;
                overflow: hidden;
                border-radius: var(--joy-core-radius-4);
                display: flex;
                align-items: center;
                justify-content: center;
                min-height: 250px;

                @include mq.screen_md(min) {
                    min-height: 420px;
                }
            }

            &__backdrop {
                position: absolute;
                inset: 0;
                pointer-events: none;
                filter: blur(var(--mui-background-blur));
                transition: all var(--joy-transition-duration-default) var(--joy-transition-timing-function);
            }

            &:hover {
                .slice-embeded--embeded-demo__backdrop {
                    inset: -12px;
                    filter: blur(var(--mui-background-blur-hover));
                }
            }

            &__background {
                position: absolute;
                inset: 0;
            }

            .mui-shape {
                top: 0;
                right: 450px;
            }

            &__cover {
                position: absolute;
                top: 50%;
                left: 50%;
                transform: translate(-50%, -50%);
                max-height: calc(100% - (var(--joy-core-spacing-6) * 2));
                max-width: calc(100% - (var(--joy-core-spacing-6) * 2));
                object-fit: cover;
                border-radius: var(--joy-core-radius-3);
            }

            &__content {
                position: relative;
                z-index: 1;
                display: flex;
                flex-direction: column;
                align-items: center;
                justify-content: center;
                gap: var(--joy-core-spacing-5);
                padding: var(--joy-core-spacing-6);
                text-align: center;

                @include mq.screen_sm(min) {
                    max-width: 640px;
                }
            }

            .slice-title {
                margin: 0;

                h2 {
                    @include text.getFontBySize(secondary-200);

                    color: var(--joy-color-neutral-60);

                    @include mq.screen_sm(min) {
                        @include text.getFontBySize(secondary-400);
                    }
                }
            }

            .slice-description {
                margin: 0;
                color: var(--joy-color-text-default);

                p {
                    margin: 0;

                    @include text.getFontBySize(primary-400);

                    @include mq.screen_sm(min) {
                        @include text.getFontBySize(primary-600);
                    }
                }
            }

            &__iframe {
                width: 100%;
                height: 100%;
                border: none;
            }
        }
    }
</style>
